import React, { InputHTMLAttributes, useState } from 'react';
import { RichText } from 'components/richText';
import { BaseItemFilter } from 'components/scommesseFilterTertiary/ScommesseFilterTertiary';
import { useTranslation } from 'hooks/useLingUI';
import { Controller, FieldValues, Path, UseControllerProps } from 'react-hook-form';
import { isTruthy } from 'utility/functions';
import { isControlledInput } from 'components/inputs/utils';
import styles from './ToggleCheckBox.module.scss';

export type ToggleCheckBoxProps = BaseItemFilter & {
  isChecked?: boolean;
  className?: string;
  hasNoLabel?: boolean;
  hasMarginTop?: boolean;
  hasMarginLeft?: boolean;
  isLabelBefore?: boolean;
  hasMarginRight?: boolean;
  hasMarginBottom?: boolean;
  handleFilterChange: Function;
  errorMessage?: string;
} & InputHTMLAttributes<HTMLInputElement>;

export type ControlledToggleCheckBoxProps<T extends FieldValues = FieldValues, K extends Path<T> = Path<T>> = Omit<
  ToggleCheckBoxProps,
  'handleFilterChange'
> &
  UseControllerProps<T, K>;

export function ToggleCheckBox<T extends FieldValues = FieldValues>(_: ControlledToggleCheckBoxProps<T>): JSX.Element;
export function ToggleCheckBox(_: ToggleCheckBoxProps): JSX.Element;
export function ToggleCheckBox(props: ToggleCheckBoxProps | ControlledToggleCheckBoxProps): JSX.Element {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState<boolean>(props?.isChecked || false);
  const { hasMarginTop, hasMarginRight, hasMarginBottom, hasMarginLeft, ...commonProps } = props;

  const text = isTruthy(props?.label?.length) ? t(props.labelTrKey, props.label) : props.value;

  if (isControlledInput<ControlledToggleCheckBoxProps>(props)) {
    return (
      <Controller
        control={props.control}
        key={`${props.name}_controller_toggle`}
        name={props.name}
        rules={props.rules}
        defaultValue={props.defaultValue}
        render={({ field }) => (
          <div
            className={`${styles.toggleForm}
                ${hasMarginTop ? styles.containerMarginTop : ''}
                ${hasMarginRight ? styles.containerMarginRight : ''}
                ${hasMarginBottom ? styles.containerMarginBottom : ''}
                ${hasMarginLeft ? styles.containerMarginLeft : ''} 
                ${props.className}
              `}
          >
            <label htmlFor={props.value} className={`${styles.label} ${props.hasNoLabel ? styles.noPadding : ''}`}>
              <input
                {...commonProps}
                type="checkbox"
                id={props.value}
                name={props.value}
                checked={field.value || isChecked || false}
                value={field.value || false}
                ref={field.ref}
                onChange={(e) => {
                  field.onChange(e);
                  props.onChange && props.onChange(e);
                }}
                onBlur={(e) => {
                  field.onBlur();
                  props.onBlur && props.onBlur(e);
                }}
              />
            </label>
            {!props.hasNoLabel && (
              <RichText tag="span" containerClassName={styles.togglelabelText}>
                {text}
              </RichText>
            )}
            {props?.errorMessage && <span className={styles.errorMessage}>{props?.errorMessage}&nbsp;</span>}
          </div>
        )}
      />
    );
  }

  return (
    <div
      className={`${styles.toggleForm}
    ${props.hasMarginTop ? styles.containerMarginTop : ''}
    ${props.hasMarginRight ? styles.containerMarginRight : ''}
    ${props.hasMarginBottom ? styles.containerMarginBottom : ''}
    ${props.hasMarginLeft ? styles.containerMarginLeft : ''} 
    ${props.isLabelBefore ? styles.invertToggleForm : ''}
    ${props.className && props.className}`}
    >
      <label htmlFor={props.value} className={`${styles.label} ${props.hasNoLabel ? styles.noPadding : ''}`}>
        <input
          type="checkbox"
          id={props.value}
          name={props.value}
          checked={isChecked}
          onChange={(e) => {
            setIsChecked(!isChecked);
            props.handleFilterChange && props.handleFilterChange(e.target);
          }}
        />
      </label>
      {!props.hasNoLabel && (
        <RichText tag="span" containerClassName={styles.togglelabelText}>
          {text}
        </RichText>
      )}
    </div>
  );
}